import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import {
  ALL_TIME_PRESET,
  CUSTOM_PRESET,
  getPeriodPresetGroups,
  resolveCurrentRange,
  resolvePreviousRange,
  formatRangeLabel,
} from "./periodPresets";

// Settings-form building blocks shared by Aggregation KPI and Efficiency KPI
// (both tiles' user stories require these to behave identically) — keep
// changes here in sync for both consumers rather than forking per tile.

// ── Section header ───────────────────────────────────────────────────────
export const SectionHeader = ({ title, subtitle }) => (
  <div className="border-t border-gray-100 pt-4">
    <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
    {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
  </div>
);

// ── Reusable activity include/exclude block ────────────────────────────────
export const ActivityFilterBlock = ({ filterKey, localFilters, updateLocalFilter, availableActivities, loading }) => {
  const included = localFilters?.[filterKey]?.included || [];
  const excluded = localFilters?.[filterKey]?.excluded || [];

  const handleIncludedChange = (vals) => {
    updateLocalFilter(filterKey, {
      included: vals,
      excluded,
      enabled: vals.length > 0 || excluded.length > 0,
    });
  };

  const handleExcludedChange = (vals) => {
    updateLocalFilter(filterKey, {
      included,
      excluded: vals,
      enabled: included.length > 0 || vals.length > 0,
    });
  };

  return (
    <div className="space-y-3">
      <MultiSelectDropdown
        label="Einschließen — Objekte mit einer dieser Aktivitäten:"
        options={availableActivities.filter((a) => !excluded.includes(a))}
        selectedValues={included}
        onSelectionChange={handleIncludedChange}
        placeholder="Aktivitäten auswählen..."
        disabled={loading}
        itemType="activities"
      />
      <MultiSelectDropdown
        label="Ausschließen — Objekte mit einer dieser Aktivitäten:"
        options={availableActivities.filter((a) => !included.includes(a))}
        selectedValues={excluded}
        onSelectionChange={handleExcludedChange}
        placeholder="Aktivitäten auswählen..."
        disabled={loading}
        itemType="activities"
      />
    </div>
  );
};

// ── Betrachtungszeitraum preset bar ─────────────────────────────────────────
const PERIOD_BUTTON_CLASS = (active) =>
  `px-3 py-1.5 text-xs rounded-md border transition-colors ${
    active
      ? "bg-blue-600 text-white border-blue-600"
      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
  }`;

const PeriodPresetButton = ({ presetKey, label, active, onClick }) => (
  <button type="button" onClick={() => onClick(presetKey)} className={PERIOD_BUTTON_CLASS(active)}>
    {label}
  </button>
);

// ── Betrachtungszeitraum section (preset bar + Custom Von/Bis + preview) ──
export const BetrachtungszeitraumSection = ({
  periodPreset,
  onPeriodPresetChange,
  customStart,
  onCustomStartChange,
  customEnd,
  onCustomEndChange,
  datasetMin,
  datasetMax,
  comparisonEnabled,
}) => {
  const { t } = useTranslation();

  // Native 'change' listener (fires on picker-close/blur, not per keystroke)
  // so React re-renders from the parent draft don't reset Chrome's internal
  // date-section cursor while typing.
  const startRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    const el = startRef.current;
    if (!el) return;
    const handle = () => onCustomStartChange(el.value || null);
    el.addEventListener("change", handle);
    return () => el.removeEventListener("change", handle);
  }, [onCustomStartChange]);

  useEffect(() => {
    const el = endRef.current;
    if (!el) return;
    const handle = () => onCustomEndChange(el.value || null);
    el.addEventListener("change", handle);
    return () => el.removeEventListener("change", handle);
  }, [onCustomEndChange]);

  // Live preview of the resolved current/previous ranges, shown under the preset bar.
  const currentRangePreview = useMemo(
    () => resolveCurrentRange(periodPreset, { customStart, customEnd }),
    [periodPreset, customStart, customEnd]
  );
  const previewLabel = formatRangeLabel(currentRangePreview);
  const previousRangePreview = useMemo(
    () => resolvePreviousRange(periodPreset, currentRangePreview),
    [periodPreset, currentRangePreview]
  );
  const previousPreviewLabel = previousRangePreview ? formatRangeLabel(previousRangePreview) : null;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Betrachtungszeitraum
        {datasetMin && datasetMax && (
          <span className="text-xs text-gray-400 font-normal ml-2">({datasetMin} – {datasetMax})</span>
        )}
      </label>

      <div className="flex flex-wrap gap-2 mb-3">
        <PeriodPresetButton
          presetKey={ALL_TIME_PRESET.key}
          label={ALL_TIME_PRESET.label}
          active={periodPreset === ALL_TIME_PRESET.key}
          onClick={onPeriodPresetChange}
        />
        <PeriodPresetButton
          presetKey={CUSTOM_PRESET.key}
          label={CUSTOM_PRESET.label}
          active={periodPreset === CUSTOM_PRESET.key}
          onClick={onPeriodPresetChange}
        />
      </div>

      {getPeriodPresetGroups(t).map((group) => (
        <div key={group.id} className="space-y-1.5 mb-3">
          <label className="block text-xs font-medium text-gray-500">{group.label}</label>
          <div className="flex flex-wrap gap-2">
            {group.presets.map((p) => (
              <PeriodPresetButton
                key={p.key}
                presetKey={p.key}
                label={p.label}
                active={periodPreset === p.key}
                onClick={onPeriodPresetChange}
              />
            ))}
          </div>
        </div>
      ))}

      {periodPreset === CUSTOM_PRESET.key && (
        <div className="grid grid-cols-2 gap-3 mt-1">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Von</label>
            <input
              ref={startRef}
              type="date"
              defaultValue={customStart || ""}
              min={datasetMin}
              max={datasetMax}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Bis</label>
            <input
              ref={endRef}
              type="date"
              defaultValue={customEnd || ""}
              min={datasetMin}
              max={datasetMax}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      {previewLabel && (
        <p className="text-xs text-gray-500 mt-2">Zeitraum: {previewLabel}</p>
      )}
      {comparisonEnabled && previousPreviewLabel && (
        <p className="text-xs text-gray-500 mt-1">Vorperiode: {previousPreviewLabel}</p>
      )}
    </div>
  );
};

// ── Vorperioden-Vergleich section ───────────────────────────────────────────
export const ComparisonSection = ({
  periodPreset,
  comparisonEnabled,
  onComparisonEnabledChange,
  trendDirection,
  onTrendDirectionChange,
  loading,
  radioGroupName = "trendDirection",
}) => {
  const disabledByAllTime = periodPreset === ALL_TIME_PRESET.key;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <label
        className={`flex items-center gap-2 px-3 py-2.5 bg-gray-50 transition-colors ${
          disabledByAllTime ? "opacity-60" : "cursor-pointer hover:bg-gray-100"
        }`}
      >
        <input
          type="checkbox"
          checked={comparisonEnabled && !disabledByAllTime}
          onChange={(e) => onComparisonEnabledChange(e.target.checked)}
          disabled={loading || disabledByAllTime}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-xs font-semibold text-gray-700">Vorperioden-Vergleich aktivieren</span>
      </label>
      {disabledByAllTime && (
        <p className="text-xs text-gray-500 px-3 py-2 bg-white">
          Bei "Alle Zeit" gibt es keine Vorperiode zum Vergleichen.
        </p>
      )}
      {comparisonEnabled && !disabledByAllTime && (
        <div className="px-3 py-3 space-y-2 bg-white">
          <label className="block text-xs font-medium text-gray-600">Trendrichtung</label>
          <div className="flex flex-col gap-1.5">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={radioGroupName}
                checked={trendDirection === "higherIsBetter"}
                onChange={() => onTrendDirectionChange("higherIsBetter")}
                disabled={loading}
                style={{ accentColor: "#0066cc" }}
              />
              <span className="text-sm text-gray-700">Höher ist besser</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={radioGroupName}
                checked={trendDirection === "lowerIsBetter"}
                onChange={() => onTrendDirectionChange("lowerIsBetter")}
                disabled={loading}
                style={{ accentColor: "#0066cc" }}
              />
              <span className="text-sm text-gray-700">Niedriger ist besser</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};
